import { TScanMutationId } from '@verifime/components';
import { createContext, ReactNode, useContext, useMemo, useReducer } from 'react';

type ScanStatus = 'SUCCEEDED' | 'FAILED' | 'BYPASSED' | null;

type ScanState = {
  scanStatus: ScanStatus;
  scanMutationId: TScanMutationId;
};

type ScanAction =
  | { type: 'SET_SCAN_STATUS'; payload: ScanStatus }
  | { type: 'SET_SCAN_MUTATION_ID'; payload: TScanMutationId['action'] }
  | { type: 'RESET' };

const initialState: ScanState = {
  scanStatus: null,
  scanMutationId: { id: 0 },
};

function scanReducer(state: ScanState, action: ScanAction): ScanState {
  switch (action.type) {
    case 'SET_SCAN_STATUS':
      return { ...state, scanStatus: action.payload };
    case 'SET_SCAN_MUTATION_ID':
      return {
        ...state,
        scanMutationId: { id: state.scanMutationId.id + 1, action: action.payload },
      };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

type ScanContextValue = {
  state: ScanState;
  scanSuccessed: () => void;
  scanFailed: () => void;
  resetScanStatus: () => void;
  cancelScan: () => void;
  startScan: () => void;
  bypassScan: () => void;
  resetScan: () => void;
};

const ScanContext = createContext<ScanContextValue | undefined>(undefined);

export const ScanProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(scanReducer, initialState);
  const { scanStatus, scanMutationId } = state;

  const scanSuccessed = () => {
    dispatch({ type: 'SET_SCAN_STATUS', payload: 'SUCCEEDED' });
  };

  const scanFailed = () => {
    dispatch({ type: 'SET_SCAN_STATUS', payload: 'FAILED' });
  };

  const resetScanStatus = () => {
    dispatch({ type: 'SET_SCAN_STATUS', payload: null });
  };

  const cancelScan = () => {
    dispatch({ type: 'SET_SCAN_MUTATION_ID', payload: 'CANCEL' });
  };

  const startScan = () => {
    dispatch({ type: 'SET_SCAN_MUTATION_ID', payload: 'SCAN' });
  };

  const bypassScan = () => {
    dispatch({ type: 'SET_SCAN_STATUS', payload: 'BYPASSED' });
  };

  const resetScan = () => {
    dispatch({ type: 'RESET' });
  };

  const value: ScanContextValue = useMemo(
    () => ({
      state: {
        scanStatus,
        scanMutationId,
      },
      scanSuccessed,
      scanFailed,
      resetScanStatus,
      cancelScan,
      startScan,
      bypassScan,
      resetScan,
    }),
    [scanStatus, scanMutationId],
  );

  return <ScanContext.Provider value={value}>{children}</ScanContext.Provider>;
};

// Hook to use the context
export const useScanContext = (): ScanContextValue => {
  const context = useContext(ScanContext);
  if (context === undefined) {
    throw new Error('useScanContext must be used within a ScanProvider');
  }
  return context;
};

// Export types for external use
export type { ScanContextValue, ScanStatus };
